// src/components/home/Hero.js
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import "../../index.css";

function Hero() {
  const allocators = [
    "Family Offices / Private Funds",
    "Wealth Managers",
    "Fund Distributors",
    "HNWIs",
    "Private Individuals",
    "Institutional Investor"
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(
      () => setIndex((prev) => (prev + 1) % allocators.length),
      2500 // change every 2.5 seconds
    );
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative bg-gradient-to-b from-[#050E24] via-[#0A2A66] to-[#050E24] text-white text-center py-44 px-6 overflow-hidden">
      {/* Animated background layers */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/3 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-2xl animate-pulse delay-700"></div>
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,255,255,0.05),rgba(0,0,255,0.05),rgba(0,255,255,0.05))] bg-[length:200%_200%] animate-[gradientShift_12s_linear_infinite]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Headline */}
        <motion.h1
          className="text-4xl md:text-6xl font-extrabold mb-4 tracking-tight leading-tight"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          Institutional-Grade{" "}
          <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent drop-shadow-[0_0_25px_rgba(0,200,255,0.25)] animate-pulse">
            DeFi Hedge Fund
          </span>
        </motion.h1>

        {/* Glowing Divider */}
        <motion.div
          className="w-24 h-[2px] bg-gradient-to-r from-cyan-400 to-blue-400 mx-auto mb-6 rounded-full opacity-80"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />

        {/* Subheadline with animated allocators */}
        <div className="text-base md:text-lg text-gray-300 tracking-wide uppercase mb-8 flex flex-col items-center">
          <span>Non-custodial hedge fund strategies built for</span>
          <div className="h-6 md:h-7 mt-1 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.span
                key={allocators[index]}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5 }}
                className="inline-block bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent font-semibold"
              >
                {allocators[index]}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>

        {/* Description */}
        <motion.p
          className="text-lg md:text-xl max-w-3xl mx-auto text-gray-200 mb-12 leading-relaxed"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.45 }}
        >
          QuantNexus Capital operates fully automated, on-chain hedge fund vaults built on Enzyme and secured by Kraken — delivering institutional-grade execution, transparency, and performance with zero custody or counterparty risk.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
        >
          {/* Primary CTA */}
          <Link
            to="/investors"
            className="group relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-semibold text-lg rounded-xl text-white bg-gradient-to-r from-cyan-500 via-blue-500 to-blue-700 shadow-[0_0_20px_rgba(0,200,255,0.2)] hover:shadow-[0_0_35px_rgba(0,200,255,0.35)] transition-all duration-500 hover:scale-[1.03]"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-0 group-hover:opacity-30 blur-xl transition duration-500"></span>
            <span className="relative z-10">For Private Investors</span>
          </Link>

          {/* Secondary CTA */}
          <Link
            to="/allocators"
            className="group relative inline-flex items-center justify-center px-10 py-4 font-medium text-lg rounded-xl text-white/90 border border-white/20 backdrop-blur-md bg-white/5 hover:bg-white/10 hover:border-cyan-400/60 transition-all duration-500 hover:scale-[1.02]"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent opacity-0 group-hover:opacity-100 animate-gradient-sweep rounded-xl"></span>
            <span className="relative z-10">For Allocators</span>
          </Link>

          {/* Tertiary CTA */}
          <Link
            to="/partnerships"
            className="group relative inline-flex items-center justify-center px-10 py-4 font-medium text-lg rounded-xl text-white/90 border border-white/20 backdrop-blur-md bg-white/5 hover:bg-white/10 hover:border-blue-400/60 transition-all duration-500 hover:scale-[1.02]"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/10 to-transparent opacity-0 group-hover:opacity-100 animate-gradient-sweep rounded-xl"></span>
            <span className="relative z-10">For Strategy Partners</span>
          </Link>
        </motion.div>


        {/* Supporting note */}
        <motion.p
          className="mt-14 text-sm text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.8 }}
        >
          Allocations $500K–$5M • Vaults live in 48h • On-chain transparency • Custody by Kraken
        </motion.p>
      </div>
    </section>
  );
}

export default Hero;
